import { useCallback, useEffect, useState } from "react";
import { Link, useLocation } from "wouter";
import {
	ApiError,
	type CastMe,
	type DiaryEntry,
	deleteDiary,
	fetchMe,
	getAccessToken,
	listMyDiaries,
	postDiary,
	updateDiary,
} from "@/lib/api.ts";

type State =
	| { kind: "loading" }
	| { kind: "ready"; cast: CastMe; diaries: DiaryEntry[] }
	| { kind: "error"; message: string };

export function Diary() {
	const [, setLocation] = useLocation();
	const [state, setState] = useState<State>({ kind: "loading" });
	const [title, setTitle] = useState("");
	const [content, setContent] = useState("");
	const [submitting, setSubmitting] = useState(false);
	const [flash, setFlash] = useState<string | null>(null);
	const [editingId, setEditingId] = useState<string | null>(null);
	const [editTitle, setEditTitle] = useState("");
	const [editContent, setEditContent] = useState("");
	const [deletingId, setDeletingId] = useState<string | null>(null);

	const reloadDiaries = useCallback(async (castId: string): Promise<DiaryEntry[]> => {
		return listMyDiaries(castId);
	}, []);

	useEffect(() => {
		if (!getAccessToken()) {
			setLocation("/");
			return;
		}
		let cancelled = false;
		(async () => {
			try {
				const cast = await fetchMe();
				const diaries = await reloadDiaries(cast.id);
				if (!cancelled) setState({ kind: "ready", cast, diaries });
			} catch (err) {
				if (cancelled) return;
				if (err instanceof ApiError && err.status === 401) {
					setLocation("/");
					return;
				}
				setState({
					kind: "error",
					message: err instanceof Error ? err.message : "予期しないエラーが発生しました",
				});
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [setLocation, reloadDiaries]);

	if (state.kind === "loading") {
		return (
			<main className="flex min-h-screen items-center justify-center">
				<p className="text-sm text-gray-600">読み込み中...</p>
			</main>
		);
	}

	if (state.kind === "error") {
		return (
			<main className="mx-auto max-w-md px-4 py-16">
				<div className="rounded-2xl bg-white p-6 shadow-sm">
					<h1 className="text-lg font-semibold">エラー</h1>
					<p className="mt-3 text-sm text-gray-700">{state.message}</p>
				</div>
			</main>
		);
	}

	function flashOnce(message: string) {
		setFlash(message);
		setTimeout(() => setFlash(null), 2000);
	}

	async function onSubmit(e: React.FormEvent) {
		e.preventDefault();
		if (state.kind !== "ready") return;
		setSubmitting(true);
		try {
			await postDiary(state.cast.id, { title, content });
			const diaries = await reloadDiaries(state.cast.id);
			setState({ kind: "ready", cast: state.cast, diaries });
			setTitle("");
			setContent("");
			flashOnce("下書きを保存しました");
		} catch (err) {
			setState({
				kind: "error",
				message: err instanceof Error ? err.message : "投稿に失敗しました",
			});
		} finally {
			setSubmitting(false);
		}
	}

	function startEdit(d: DiaryEntry) {
		setDeletingId(null);
		setEditingId(d.id);
		setEditTitle(d.title);
		setEditContent(d.content);
	}

	function cancelEdit() {
		setEditingId(null);
	}

	async function saveEdit(diaryId: string) {
		if (state.kind !== "ready") return;
		try {
			await updateDiary(diaryId, { title: editTitle, content: editContent });
			const diaries = await reloadDiaries(state.cast.id);
			setState({ kind: "ready", cast: state.cast, diaries });
			setEditingId(null);
			flashOnce("更新しました");
		} catch (err) {
			flashOnce(err instanceof Error ? err.message : "更新に失敗しました");
		}
	}

	// Two-step inline confirmation — window.confirm() is a no-op inside the
	// LIFF in-client browser, which does not support blocking dialogs (#11).
	function requestDelete(diaryEntry: DiaryEntry) {
		setEditingId(null);
		setDeletingId(diaryEntry.id);
	}

	function cancelDelete() {
		setDeletingId(null);
	}

	async function confirmDelete(diaryEntry: DiaryEntry) {
		if (state.kind !== "ready") return;
		try {
			await deleteDiary(diaryEntry.id);
			const diaries = await reloadDiaries(state.cast.id);
			setState({ kind: "ready", cast: state.cast, diaries });
			flashOnce("削除しました");
		} catch (err) {
			flashOnce(err instanceof Error ? err.message : "削除に失敗しました");
		} finally {
			setDeletingId(null);
		}
	}

	return (
		<main className="mx-auto max-w-md space-y-4 px-4 py-8">
			<header className="flex items-center justify-between">
				<div>
					<p className="text-xs text-gray-500">日記</p>
					<h1 className="text-xl font-semibold">{state.cast.name} さん</h1>
				</div>
				<div className="flex gap-3 text-xs text-gray-600 underline underline-offset-2">
					<Link href="/shifts">シフト</Link>
					<Link href="/profile">プロフィール</Link>
				</div>
			</header>

			<form onSubmit={onSubmit} className="space-y-4 rounded-2xl bg-white p-5 shadow-sm">
				<h2 className="text-base font-semibold">新しい日記を投稿</h2>

				<label className="block">
					<span className="text-xs text-gray-600">タイトル</span>
					<input
						type="text"
						value={title}
						onChange={(e) => setTitle(e.target.value)}
						maxLength={100}
						className="mt-1 block w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
						required
					/>
				</label>

				<label className="block">
					<span className="text-xs text-gray-600">本文</span>
					<textarea
						value={content}
						onChange={(e) => setContent(e.target.value)}
						rows={8}
						className="mt-1 block w-full resize-none rounded-lg border border-gray-200 px-3 py-2 text-sm"
						required
					/>
				</label>

				<p className="text-xs text-gray-500">
					下書きとして保存されます。公開はお店側の管理画面で行われます。
				</p>

				<button
					type="submit"
					disabled={submitting}
					className="w-full rounded-xl bg-green-600 py-3 text-sm font-semibold text-white disabled:opacity-50 active:bg-green-700"
				>
					{submitting ? "投稿中..." : "下書き保存"}
				</button>

				{flash ? <p className="text-center text-xs text-green-700">{flash}</p> : null}
			</form>

			<section className="space-y-3 rounded-2xl bg-white p-5 shadow-sm">
				<h2 className="text-base font-semibold">自分の日記一覧</h2>
				{state.diaries.length === 0 ? (
					<p className="text-xs text-gray-500">まだ日記はありません。</p>
				) : (
					<ul className="space-y-2">
						{state.diaries.map((entry) => (
							<li
								key={entry.id}
								className="rounded-xl border border-gray-100 bg-gray-50/60 p-3 text-sm"
							>
								{editingId === entry.id ? (
									<div className="space-y-2">
										<input
											type="text"
											value={editTitle}
											onChange={(e) => setEditTitle(e.target.value)}
											maxLength={100}
											className="block w-full rounded-lg border border-gray-200 px-2 py-1.5 text-sm"
										/>
										<textarea
											value={editContent}
											onChange={(e) => setEditContent(e.target.value)}
											rows={5}
											className="block w-full resize-none rounded-lg border border-gray-200 px-2 py-1.5 text-sm"
										/>
										<div className="flex gap-2">
											<button
												type="button"
												onClick={cancelEdit}
												className="flex-1 rounded-lg border border-gray-200 py-1.5 text-xs text-gray-600"
											>
												キャンセル
											</button>
											<button
												type="button"
												onClick={() => saveEdit(entry.id)}
												className="flex-1 rounded-lg bg-green-600 py-1.5 text-xs font-semibold text-white"
											>
												保存
											</button>
										</div>
									</div>
								) : deletingId === entry.id ? (
									<div className="space-y-2">
										<p className="text-xs text-gray-700">「{entry.title}」を削除しますか?</p>
										<div className="flex gap-2">
											<button
												type="button"
												onClick={cancelDelete}
												className="flex-1 rounded-lg border border-gray-200 py-1.5 text-xs text-gray-600"
											>
												キャンセル
											</button>
											<button
												type="button"
												onClick={() => confirmDelete(entry)}
												className="flex-1 rounded-lg bg-red-600 py-1.5 text-xs font-semibold text-white"
											>
												削除する
											</button>
										</div>
									</div>
								) : (
									<div className="flex items-start justify-between gap-3">
										<div className="min-w-0 flex-1">
											<p className="truncate font-medium">{entry.title}</p>
											<p className="text-xs text-gray-500">
												{formatState(entry.publishState)} ·{" "}
												{new Date(entry.updatedAt).toLocaleString("ja-JP")}
											</p>
										</div>
										<div className="flex gap-2">
											<button
												type="button"
												onClick={() => startEdit(entry)}
												className="rounded-lg border border-gray-200 px-2 py-1 text-xs text-gray-700"
											>
												編集
											</button>
											<button
												type="button"
												onClick={() => requestDelete(entry)}
												className="rounded-lg border border-red-200 px-2 py-1 text-xs text-red-600"
											>
												削除
											</button>
										</div>
									</div>
								)}
							</li>
						))}
					</ul>
				)}
			</section>
		</main>
	);
}

function formatState(s: DiaryEntry["publishState"]): string {
	if (s === "draft") return "下書き";
	if (s === "scheduled") return "予約投稿";
	return "公開済";
}
